import { useState, useEffect } from "react";
import BaseModal from "../BaseComponents/BaseModal/BaseModal";
import styles from "./ContactModal.module.css";

// ── Constants ──────────────────────────────────────────────────────────────
const CONTACT_ROLES = [
  "Accounts Manager", "CEO / Director", "Finance Manager",
  "HR Manager", "Operations Manager", "PA / Assistant",
  "Procurement Manager", "Travel Coordinator", "Other",
];

const LANGUAGES = [
  "Arabic", "Chinese", "Danish", "Dutch", "English", "Finnish",
  "French", "German", "Greek", "Hungarian", "Italian", "Japanese",
  "Korean", "Norwegian", "Polish", "Portuguese", "Romanian",
  "Russian", "Spanish", "Swedish", "Turkish", "Ukrainian",
].sort();

const emptyForm = {
  firstName: "",
  lastName:  "",
  hasEmail:  true,
  email:     "",
  phone:     "",
  role:      "",
  language:  "",
};

const REQUIRED_FIELDS = ["firstName", "lastName", "phone"];

// ── Validation ─────────────────────────────────────────────────────────────
const validateField = (key, value, form) => {
  switch (key) {
    case "firstName":
      if (!value?.trim())          return "First name is required";
      if (value.trim().length < 2) return "Must be at least 2 characters";
      break;
    case "lastName":
      if (!value?.trim()) return "Last name is required";
      break;
    case "email":
      if (form && !form.hasEmail) return ""; // contact may exist without email
      if (!value?.trim()) return "Email is required";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return "Invalid email format";
      break;
    case "phone":
      if (!value?.trim()) return "Phone number is required";
      break;
    default: break;
  }
  return "";
};

// ── Component ──────────────────────────────────────────────────────────────
const ContactModal = ({
  open,
  onClose,
  onSave,
  contact = null,       // null = Add mode, object = Edit mode
  companyName = "",     // shown in header for context
  canSetPrimary = false, // show "set as primary" toggle only in Add mode when relevant
}) => {
  const [form,        setForm]        = useState(emptyForm);
  const [errors,      setErrors]      = useState({});
  const [touched,     setTouched]     = useState({});
  const [setPrimary,  setSetPrimary]  = useState(false);

  const isEdit = !!contact;

  // ── Populate form ──
  useEffect(() => {
    if (!open) return;
    if (contact) {
      setForm({
        firstName: contact.firstName ?? "",
        lastName:  contact.lastName  ?? "",
        hasEmail:  contact.hasEmail ?? !!contact.email,
        email:     contact.email     ?? "",
        phone:     contact.phone     ?? "",
        role:      contact.role      ?? "",
        language:  contact.language  ?? "",
      });
      setSetPrimary(contact.isPrimary ?? false);
    } else {
      setForm(emptyForm);
      setSetPrimary(false);
    }
    setErrors({});
    setTouched({});
  }, [open, contact]);

  const handleChange = (key, value) => {
    // Force email to lowercase and strip stray whitespace (e.g. from pasting)
    if (key === "email") value = value.trim().toLowerCase();
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: "" }));
  };

  const handleBlur = (key) => {
    setTouched(prev => ({ ...prev, [key]: true }));
    setErrors(prev => ({ ...prev, [key]: validateField(key, form[key], form) }));
  };

  const handleClose = () => {
    setForm(emptyForm); setErrors({}); setTouched({});
    onClose();
  };

  const handleSubmit = () => {
    const newErrors = {};
    let isValid = true;
    REQUIRED_FIELDS.forEach(key => {
      const err = validateField(key, form[key], form);
      if (err) { newErrors[key] = err; isValid = false; }
    });
    // Email is only required when the contact has an email address
    if (form.hasEmail) {
      const emailErr = validateField("email", form.email, form);
      if (emailErr) { newErrors.email = emailErr; isValid = false; }
    }
    setErrors(newErrors);
    setTouched(Object.fromEntries([...REQUIRED_FIELDS, "email"].map(k => [k, true])));
    if (!isValid) return;

    onSave({
      ...(contact ?? {}),
      id:        contact?.id ?? Date.now(),
      firstName: form.firstName.trim(),
      lastName:  form.lastName.trim(),
      hasEmail:  form.hasEmail,
      email:     form.hasEmail ? form.email.trim().toLowerCase() : "",
      phone:     form.phone.trim(),
      role:      form.role     || undefined,
      language:  form.language || undefined,
      isPrimary: setPrimary,
    });

    handleClose();
  };

  const hasErrors = Object.values(errors).some(Boolean);
  const avatarLetter = form.firstName?.[0]?.toUpperCase() || "?";

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title={isEdit ? "Edit Contact" : "Add Contact"}
      width="620px"
      footer={
        <>
          <button className={styles.closeBtn} onClick={handleClose}>Cancel</button>
          <button className={styles.saveBtn} onClick={handleSubmit}>
            {isEdit ? "Save Changes" : "Add Contact"}
          </button>
        </>
      }
    >

      {/* ── Icon Header ── */}
      <div className={styles.iconHeader}>
        <div className={styles.avatarPreview}>
          {form.firstName && form.lastName
            ? `${form.firstName[0]}${form.lastName[0]}`.toUpperCase()
            : "👤"}
        </div>
        <div className={styles.headerText}>
          <h3>
            {isEdit
              ? `Edit ${contact.firstName} ${contact.lastName}`
              : "Add New Contact"}
          </h3>
          {companyName && (
            <p>
              {isEdit ? "Update contact details for" : "Adding contact to"}{" "}
              <strong>{companyName}</strong>
            </p>
          )}
        </div>
      </div>

      {/* ── Error Banner ── */}
      {hasErrors && (
        <div className={styles.errorBanner}>
          <span>⚠️</span>
          <div>
            <strong>Please fix the following:</strong>
            <ul>
              {Object.entries(errors).map(([key, err]) =>
                err ? <li key={key}>{err}</li> : null
              )}
            </ul>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Section 1 — Name                                                */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>👤 PERSONAL DETAILS</h4>
        <div className={styles.formGrid}>

          <div className={styles.field}>
            <label>FIRST NAME <span className={styles.required}>*</span></label>
            <input
              value={form.firstName}
              onChange={e => handleChange("firstName", e.target.value)}
              onBlur={() => handleBlur("firstName")}
              placeholder="e.g. Thomas"
              className={errors.firstName ? styles.inputError : ""}
            />
            {errors.firstName && <span className={styles.errorText}>{errors.firstName}</span>}
          </div>

          <div className={styles.field}>
            <label>LAST NAME <span className={styles.required}>*</span></label>
            <input
              value={form.lastName}
              onChange={e => handleChange("lastName", e.target.value)}
              onBlur={() => handleBlur("lastName")}
              placeholder="e.g. Leclercq"
              className={errors.lastName ? styles.inputError : ""}
            />
            {errors.lastName && <span className={styles.errorText}>{errors.lastName}</span>}
          </div>

          <div className={styles.field}>
            <label>ROLE / TITLE</label>
            <select value={form.role} onChange={e => handleChange("role", e.target.value)}>
              <option value="">Select role</option>
              {CONTACT_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div className={styles.field}>
            <label>PREFERRED LANGUAGE</label>
            <select value={form.language} onChange={e => handleChange("language", e.target.value)}>
              <option value="">Select language</option>
              {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>

        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Section 2 — Contact Info                                        */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>📬 CONTACT INFORMATION</h4>
        <div className={styles.formGrid}>

          <div className={styles.field}>
            <label>CONTACT HAS EMAIL ADDRESS</label>
            <div className={styles.yesNoToggle}>
              <button
                type="button"
                className={`${styles.yesNoBtn} ${form.hasEmail ? styles.yesNoBtnYes : ""}`}
                onClick={() => handleChange("hasEmail", true)}
              >YES</button>
              <button
                type="button"
                className={`${styles.yesNoBtn} ${!form.hasEmail ? styles.yesNoBtnNo : ""}`}
                onClick={() => { handleChange("hasEmail", false); handleChange("email", ""); setErrors(prev => ({ ...prev, email: "" })); }}
              >NO</button>
            </div>
          </div>

          <div className={styles.field} />

          {form.hasEmail && (
            <div className={styles.field}>
              <label>EMAIL <span className={styles.required}>*</span></label>
              <input
                type="email"
                value={form.email}
                onChange={e => handleChange("email", e.target.value)}
                onBlur={() => handleBlur("email")}
                placeholder="e.g. thomas@company.com"
                className={errors.email ? styles.inputError : ""}
              />
              {errors.email && <span className={styles.errorText}>{errors.email}</span>}
            </div>
          )}

          <div className={styles.field}>
            <label>PHONE <span className={styles.required}>*</span></label>
            <input
              type="tel"
              value={form.phone}
              onChange={e => handleChange("phone", e.target.value)}
              onBlur={() => handleBlur("phone")}
              placeholder="e.g. +32 2 234 56 78"
              className={errors.phone ? styles.inputError : ""}
            />
            {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
          </div>

        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Section 3 — Primary Contact Toggle                             */}
      {/* Only show if: adding new contact OR editing a non-primary one  */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {(!isEdit || !contact?.isPrimary) && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>⭐ PRIMARY CONTACT</h4>

          <div className={styles.primaryToggleCard}>
            <div className={styles.primaryToggleLeft}>
              <div className={styles.primaryToggleTitle}>Set as primary contact</div>
              <div className={styles.primaryToggleDesc}>
                The primary contact receives all official communication from Sunsky.
                {setPrimary && " Setting this contact as primary will unset the current primary."}
              </div>
            </div>
            <div className={styles.primaryToggleRight}>
              <input
                type="checkbox"
                id="setPrimaryToggle"
                checked={setPrimary}
                onChange={e => setSetPrimary(e.target.checked)}
                className={styles.toggleInput}
              />
              <label htmlFor="setPrimaryToggle" className={styles.toggleLabel}>
                <span className={styles.toggleSwitch} />
              </label>
            </div>
          </div>

          {setPrimary && (
            <div className={styles.primaryWarning}>
              ⚠️ The current primary contact will be demoted to a regular contact.
            </div>
          )}
        </div>
      )}

      {/* Editing the existing primary — inform user they can't unset it here */}
      {isEdit && contact?.isPrimary && (
        <div className={styles.section}>
          <div className={styles.primaryLockedCard}>
            <span>⭐</span>
            <div>
              <div className={styles.primaryLockedTitle}>This is the primary contact</div>
              <div className={styles.primaryLockedDesc}>
                To change the primary contact, set another contact as primary from the Contacts tab.
              </div>
            </div>
          </div>
        </div>
      )}

    </BaseModal>
  );
};

export default ContactModal;